import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Brain,
  Globe,
  Database,
  CheckCircle,
  AlertCircle,
  Clock,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { SearchStages } from "../hooks/useSearchProgress";
import {
  getActiveSearchProgressStage,
  getVisibleSearchProgressStages,
} from "../utils/searchProgressDisplay";

// Stage configuration
const stageConfig = {
  [SearchStages.GENERATING_QUERIES]: {
    icon: Brain,
    label: "stages.generating_queries",
    labelCompleted: "stages.generating_queries_completed",
    color: "teal",
  },
  [SearchStages.SEARCHING_PAPERS]: {
    icon: Globe,
    label: "stages.searching_papers",
    labelCompleted: "stages.searching_papers_completed",
    color: "purple",
  },
  [SearchStages.FETCHING_DETAILS]: {
    icon: Database,
    label: "stages.fetching_details",
    labelCompleted: "stages.fetching_details_completed",
    color: "indigo",
  },
  [SearchStages.ANALYZING_RESULTS]: {
    icon: Brain,
    label: "stages.analyzing_results",
    labelCompleted: "stages.analyzing_results_completed",
    color: "green",
  },
};

const searchProcessNoticeKeys = [
  "progress.searchProcessNotice.duration",
  "progress.searchProcessNotice.sequence",
  "progress.searchProcessNotice.summaryPanel",
  "progress.searchProcessNotice.keepTabOpen",
];

const StageCard = ({ stage, stageName, isActive }) => {
  const { t } = useTranslation();
  const config = stageConfig[stageName];

  if (!config) return null;

  const Icon = config.icon;
  const { status, progress: stageProgress, error, startTime, endTime } = stage;

  // Use different label based on status
  const stageLabel =
    status === "completed" && config.labelCompleted
      ? t(config.labelCompleted)
      : t(config.label);

  // Determine card styling based on status
  const getCardClasses = () => {
    if (status === "failed") {
      return "border-red-200 bg-red-50";
    }
    if (status === "completed") {
      return "border-green-200 bg-green-50";
    }
    if (isActive || status === "in_progress") {
      return "border-blue-200 bg-blue-50 ring-1 ring-blue-100";
    }
    return "border-gray-200 bg-gray-50";
  };

  const getIconClasses = () => {
    if (status === "failed") return "text-red-500";
    if (status === "completed") return "text-green-500";
    if (isActive || status === "in_progress") return "text-blue-500";
    return "text-gray-400";
  };

  const getStatusIcon = () => {
    if (status === "failed")
      return <AlertCircle className="h-4 w-4 text-red-500" />;
    if (status === "completed")
      return <CheckCircle className="h-4 w-4 text-green-500" />;
    if (isActive || status === "in_progress")
      return <Clock className="h-4 w-4 text-blue-500 animate-spin" />;
    return null;
  };

  // Calculate duration if both times are available
  const getDuration = () => {
    if (!startTime) return null;
    const end = endTime || new Date();
    const duration = Math.round((end - startTime) / 1000);
    // Only show duration if at least 1 minute has passed
    if (duration < 60) {
      return null; // Don't show seconds
    }
    const minutes = Math.round(duration / 60);
    return `${minutes}m`;
  };

  return (
    <div
      className={`rounded-lg p-3 border-2 transition-all duration-300 ${getCardClasses()}`}
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <Icon className={`h-4 w-4 ${getIconClasses()}`} />
          <span className="font-medium text-sm text-gray-900">{stageLabel}</span>
        </div>
        <div className="flex items-center space-x-2">
          {getStatusIcon()}
          {getDuration() && (
            <span className="text-xs text-gray-500">{getDuration()}</span>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {stageProgress && status === "in_progress" && (
        <div className="mt-3">
          <div className="flex justify-between text-xs text-gray-600 mb-1">
            <span>
              {stageProgress.current} / {stageProgress.total}
            </span>
            <span>{Math.round(stageProgress.percentage)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-teal-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${stageProgress.percentage}%` }}
            />
          </div>
        </div>
      )}

      {/* Error message */}
      {error && <div className="mt-2 text-sm text-red-600">{error}</div>}

      {/* Stage data summary */}
      {stage.data && (
        <div className="mt-2 space-y-1">
          {/* For generating queries stage - show first 3 queries */}
          {stage.data.queries &&
            Array.isArray(stage.data.queries) &&
            stage.data.queries.length > 0 && (
              <div className="text-xs text-gray-600">
                <div className="font-medium mb-1">
                  {t("stages.queries")} ({stage.data.queries.length}):
                </div>
                <ul className="space-y-0.5">
                  {stage.data.queries.slice(0, 3).map((query, index) => (
                    <li key={index} className="text-gray-500 truncate pl-2">
                      •{" "}
                      {query.length > 50
                        ? query.substring(0, 50) + "..."
                        : query}
                    </li>
                  ))}
                  {stage.data.queries.length > 3 && (
                    <li className="text-gray-400 italic pl-2">
                      • {stage.data.queries.length - 3} more...
                    </li>
                  )}
                </ul>
              </div>
            )}
          {/* For searching papers stage - show PMIDs */}
          {stage.data.pmids &&
            Array.isArray(stage.data.pmids) &&
            stage.data.pmids.length > 0 && (
              <div className="text-xs text-gray-600">
                <div className="font-medium mb-1">
                  {t("stages.found")}: {stage.data.pmids.length}{" "}
                  {t("stages.papers")}
                </div>
                <ul className="space-y-0.5">
                  {stage.data.pmids.slice(0, 3).map((pmid, index) => (
                    <li key={index} className="text-gray-500 pl-2">
                      • PMID: {pmid}
                    </li>
                  ))}
                  {stage.data.pmids.length > 3 && (
                    <li className="text-gray-400 italic pl-2">
                      • {stage.data.pmids.length - 3} more...
                    </li>
                  )}
                </ul>
              </div>
            )}
          {/* For fetching details stage - show first 2 paper titles */}
          {stage.data.articles &&
            Array.isArray(stage.data.articles) &&
            stage.data.articles.length > 0 && (
              <div className="text-xs text-gray-600">
                <div className="font-medium mb-1">
                  {t("stages.found")}: {stage.data.articles.length}{" "}
                  {t("stages.papers")}
                </div>
                {stage.data.articles[0]?.title && (
                  <ul className="space-y-0.5">
                    {stage.data.articles.slice(0, 2).map((article, index) => (
                      <li key={index} className="text-gray-500 truncate pl-2">
                        •{" "}
                        {article.title && article.title.length > 45
                          ? article.title.substring(0, 45) + "..."
                          : article.title || "Untitled"}
                      </li>
                    ))}
                    {stage.data.articles.length > 2 && (
                      <li className="text-gray-400 italic pl-2">
                        • {stage.data.articles.length - 2} more...
                      </li>
                    )}
                  </ul>
                )}
              </div>
            )}
          {/* For analyzing results stage - show analyzed count and top papers */}
          {stage.data.analysis &&
            Array.isArray(stage.data.analysis) &&
            stage.data.analysis.length > 0 && (
              <div className="text-xs text-gray-600">
                <div className="font-medium mb-1">
                  {t("stages.analyzed")}: {stage.data.analysis.length}{" "}
                  {t("stages.papers")}
                </div>
                {stage.data.analysis[0]?.title && (
                  <ul className="space-y-0.5">
                    {stage.data.analysis.slice(0, 2).map((paper, index) => (
                      <li key={index} className="text-gray-500 truncate pl-2">
                        •{" "}
                        {paper.title && paper.title.length > 45
                          ? paper.title.substring(0, 45) + "..."
                          : paper.title || "Untitled"}
                        {paper.relevance_score !== undefined && paper.relevance_score !== null && (
                          <span className="text-green-600 ml-1">
                            ({Number(paper.relevance_score).toFixed(1)}/10)
                          </span>
                        )}
                      </li>
                    ))}
                    {stage.data.analysis.length > 2 && (
                      <li className="text-gray-400 italic pl-2">
                        • {stage.data.analysis.length - 2} more...
                      </li>
                    )}
                  </ul>
                )}
              </div>
            )}
          {/* Generic count display - fallback */}
          {stage.data.count !== undefined &&
            !stage.data.queries &&
            !stage.data.pmids &&
            !stage.data.articles &&
            !stage.data.analysis && (
              <div className="text-xs text-gray-600">
                <span className="font-medium">{t("stages.found")}:</span>{" "}
                {stage.data.count} {t("stages.items")}
              </div>
            )}
        </div>
      )}
    </div>
  );
};

const OverallProgress = ({ percentage }) => {
  const { t } = useTranslation();

  return (
    <div className="mt-4 pt-4 border-t border-gray-200">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-gray-700">
          {t("progress.overall", "Overall Progress")}
        </span>
        <span className="text-sm text-gray-600">{Math.round(percentage)}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-3">
        <div
          className="bg-gradient-to-r from-teal-400 to-green-500 h-3 rounded-full transition-all duration-500"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

const SearchProgressIndicator = ({ progress }) => {
  const { t } = useTranslation();
  const stageOrder = getVisibleSearchProgressStages(progress);
  const overallProgressPercentage = (() => {
    if (!stageOrder.length) {
      return progress.overallProgress;
    }
    const completedCount = stageOrder.filter(
      (stageName) => progress.stages[stageName]?.status === "completed",
    ).length;
    return (completedCount / stageOrder.length) * 100;
  })();

  // Initialize collapsed state based on search status
  // Expanded during search, collapsed after completion
  const [isCollapsed, setIsCollapsed] = useState(false);

  const activeStageName = getActiveSearchProgressStage(progress);
  const shouldShowSearchProcessNotice =
    progress.isSearching &&
    progress.currentStage !== SearchStages.COMPLETED &&
    progress.currentStage !== SearchStages.FAILED;

  // Update collapsed state when search status changes
  useEffect(() => {
    if (progress.isSearching) {
      // Expand during search
      setIsCollapsed(false);
    } else if (progress.currentStage === SearchStages.COMPLETED) {
      // Collapse after completion
      setIsCollapsed(true);
    }
  }, [progress.isSearching, progress.currentStage]);

  // Show progress indicator while actively searching, even before the backend
  // returns a concrete run id, and keep it available for completed searches.
  if (!progress.searchId && !progress.isSearching) {
    return null;
  }

  return (
    <div className="search-progress-indicator bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      {/* Header - Always visible */}
      <div
        className={`flex items-start justify-between ${isCollapsed ? "" : "mb-6"}`}
      >
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">
            {t("progress.title", "Search Progress")}
          </h3>

          {/* Show summary when collapsed */}
          {isCollapsed && (
            <div className="mt-2 flex items-center space-x-4 text-sm">
              {progress.currentStage === SearchStages.COMPLETED ? (
                <span className="text-green-600 font-medium flex items-center">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  {t("progress.completed", "Search completed successfully!")}
                </span>
              ) : progress.currentStage === SearchStages.FAILED ? (
                <span className="text-red-600 font-medium flex items-center">
                  <AlertCircle className="h-4 w-4 mr-1" />
                  {t("progress.failed", "Search failed")}
                </span>
              ) : (
                <span className="text-teal-600 font-medium flex items-center">
                  <Clock className="h-4 w-4 mr-1 animate-spin" />
                  {t(`stages.${activeStageName || progress.currentStage}`)}
                </span>
              )}
              <span className="text-gray-500">
                {t("progress.overall", "Progress")}:{" "}
                {Math.round(overallProgressPercentage)}%
              </span>
            </div>
          )}
        </div>

        {/* Collapse/Expand button */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="ml-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
          aria-label={isCollapsed ? "Expand" : "Collapse"}
        >
          {isCollapsed ? (
            <ChevronDown className="h-5 w-5 text-gray-600" />
          ) : (
            <ChevronUp className="h-5 w-5 text-gray-600" />
          )}
        </button>
      </div>

      {/* Collapsible content */}
      {!isCollapsed && (
        <div className="space-y-6">
          {/* Stage cards */}
          <div className="grid grid-cols-4 gap-3">
            {Object.entries(stageConfig).map(([stageName]) => (
              <StageCard
                key={stageName}
                stage={progress.stages[stageName]}
                stageName={stageName}
                isActive={progress.isSearching && activeStageName === stageName}
              />
            ))}
          </div>

          {/* Overall progress */}
          <OverallProgress percentage={overallProgressPercentage} />

          {/* Status message */}
          {progress.currentStage && (
            <div className="mt-4 text-center text-sm text-gray-600">
              {progress.currentStage === SearchStages.COMPLETED ? (
                <span className="text-green-600 font-medium">
                  {t("progress.completed", "Search completed successfully!")}
                </span>
              ) : progress.currentStage === SearchStages.FAILED ? (
                <span className="text-red-600 font-medium">
                  {t("progress.failed", "Search failed. Please try again.")}
                </span>
              ) : (
                <span>
                  {t("progress.current_stage", "Current stage")}:{" "}
                  {t(`stages.${activeStageName || progress.currentStage}`)}
                </span>
              )}
              {shouldShowSearchProcessNotice && (
                <div className="mt-3 rounded-lg bg-teal-50 px-4 py-3 text-left text-sm leading-6 text-teal-900">
                  {searchProcessNoticeKeys.map((key) => (
                    <p key={key}>{t(key)}</p>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchProgressIndicator;
